import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Builds an AS400 contract load file from a persisted SPA record.
 *
 * Replaces the `ALL_IN_ONE` macro in `Contract Status Report.xlsm`, which asks
 * roughly 22 questions through dialog boxes and writes an 82-column
 * UPLOAD.csv. Most of those answers are already on the SPA, so this asks only
 * for what the document cannot know.
 */
export const As400BranchSchema = z.object({
  /** Stable key, e.g. "WG&SUDBURY". */
  index: z.string(),
  fullName: z.string(),
  abbreviation: z.string(),
  hasEdiBranchCode: z.boolean(),
});

/** The divisions a contract load can be built for. */
export const As400DivisionSchema = z.enum(["W+", "WJ", "WB", "QE"]);

export const As400LoadOptionsInputSchema = z.object({
  division: As400DivisionSchema,
});

export const As400LoadOptionsOutputSchema = z.object({
  branches: z.array(As400BranchSchema),
});

export const As400LoadPrefillInputSchema = z.object({ spaId: z.uuid() });

/** What the 845 can answer before the analyst types anything. */
export const As400LoadPrefillOutputSchema = z.object({
  /** Null unless every distributor branch the 845 names is in one division. */
  suggestedDivision: As400DivisionSchema.nullable(),
  /** The End Customer's name as the 845 states it. */
  customerName: z.string(),
  /** The End Customer's BP ID — a hint for finding the Rexel customer number. */
  endCustomerBpId: z.string().nullable(),
  /** Why no load can be built from this SPA, or null when one can. */
  blocker: z.string().nullable(),
});

export const As400LoadConfigInputSchema = z.object({
  spaId: z.uuid(),
  division: As400DivisionSchema,
  loadingType: z.enum(["C", "E", "J", "P"]),
  agreementType: z.enum(["A", "B", "C", "D", "E", "F"]),
  otherDescription3: z.string().optional(),
  otherReference: z.string().optional(),
  loadNumber: z.number().int().positive().default(1),
  previousSpaNumber: z.string().optional(),
  requisitionComment: z.string().default(""),
  /** Overrides the End Customer name the 845 states. */
  customerName: z.string().optional(),
  customerNumber: z.string().optional(),
  contractGroup: z.string().optional(),
  customerClass: z.string().optional(),
  territory: z.string().optional(),
  branchIndex: z.string().optional(),
  /** Target margin as a percentage: 27.2 means 27.2%. */
  defaultMarginPercent: z.number().min(0).max(99.99).default(0),
  /** Per-line overrides keyed by SPA line item id, also a percentage. */
  marginOverrides: z.record(z.uuid(), z.number().min(0).max(99.99)).default({}),
  costAdderPercent: z.number().default(0),
  applyAdderToSell: z.boolean().default(false),
  roundup: z.boolean().default(false),
});

export const As400LoadLinePreviewSchema = z.object({
  lineItemId: z.string(),
  productNumber: z.string(),
  priceClass: z.string(),
  priceSubClass: z.string(),
  costDiscount: z.number(),
  sellDiscount: z.number(),
  /** True when the sell side came from a stated resale price, not the margin. */
  sellFromDocument: z.boolean(),
  marginPercent: z.number(),
});

export const As400LoadPreviewOutputSchema = z.object({
  /** Header fields we derived, so the form can show what it will write. */
  derived: z.object({
    spaNumber: z.string(),
    customerName: z.string(),
    startDate: z.string(),
    endDate: z.string(),
    description2: z.string(),
    description3: z.string(),
  }),
  lines: z.array(As400LoadLinePreviewSchema),
  skipped: z.array(
    z.object({
      lineItemId: z.string(),
      productNumber: z.string(),
      reason: z.string(),
    }),
  ),
  /** The first rows exactly as they will appear in the file. */
  sampleRows: z.array(z.array(z.string())),
  totalRows: z.number().int(),
});

export const As400LoadGenerateOutputSchema = z.object({
  fileName: z.string(),
  contentType: z.literal("text/csv"),
  content: z.string(),
  totalRows: z.number().int(),
  skippedCount: z.number().int(),
});

export const as400LoadContracts = {
  /** Branch list for the division picker. */
  options: oc
    .input(As400LoadOptionsInputSchema)
    .output(As400LoadOptionsOutputSchema)
    .route({
      method: "GET",
      summary: "List branches for a contract load division",
      description:
        "Returns the branches available for one division, used to pick a branch when building an ERP contract price load file from a SPA.",
    }),

  /** Pre-fills the form from the SPA and its 845, once, when it opens. */
  prefill: oc
    .input(As400LoadPrefillInputSchema)
    .output(As400LoadPrefillOutputSchema)
    .route({
      method: "GET",
      summary: "Pre-fill a SPA contract price load",
      description:
        "Reads a SPA and its source 845 for what the contract price load form can pre-fill: the division its distributor branches belong to, the End Customer name and BP ID, and any reason no load can be built from it, such as a fixed-value agreement.",
    }),

  /** Assemble without persisting, so the form can show the result first. */
  preview: oc
    .input(As400LoadConfigInputSchema)
    .output(As400LoadPreviewOutputSchema)
    .route({
      method: "POST",
      summary: "Preview a SPA contract price load",
      description:
        "Builds the ERP contract load for a SPA from division, loading type, margin and branch settings without saving; returns derived header fields, per-line discounts, skipped lines, and sample rows.",
    }),

  /** The finished file. */
  generate: oc
    .input(As400LoadConfigInputSchema)
    .output(As400LoadGenerateOutputSchema)
    .route({
      method: "POST",
      summary: "Generate a SPA contract price load file",
      description:
        "Builds the ERP contract price load CSV for a SPA from the chosen load settings and returns the file content for download. Nothing is saved or sent to the ERP.",
    }),
} as const;

export type As400Branch = z.infer<typeof As400BranchSchema>;
export type As400LoadPrefillOutput = z.infer<
  typeof As400LoadPrefillOutputSchema
>;
export type As400LoadConfigInput = z.infer<typeof As400LoadConfigInputSchema>;
export type As400LoadLinePreview = z.infer<typeof As400LoadLinePreviewSchema>;
export type As400LoadPreviewOutput = z.infer<
  typeof As400LoadPreviewOutputSchema
>;
